import React, {useState} from 'react';

import placeholder from 'assets/icons/colored/pic-placeholder.svg';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';

import bem from 'client/services/bem';
import {createObjectUrl} from 'client/services/helpers';
import {useLanguage} from 'client/services/hooks';
import {getImageProperties} from 'client/services/utils';

import AppButton from 'client/common/buttons/app-button';
import FileButton from 'client/common/buttons/file-button';
import ImageContainer from 'client/common/image-container';
import InputWrapper, {InputWrapperProps} from 'client/common/inputs/input-wrapper';
import ConfirmationModal from 'client/common/modals/confirmation-modal';
import ConfirmationPopover from 'client/common/popovers/confirmation-popover';

import {Translation} from 'client/models/language/types';

import validate from './validate';

import cssModule from './image-input.module.scss';

const b = bem('image-input', {cssModule});

type ImageInputChecks = {
  checks: {
    check_error_size: number;
    check_error_width: number;
    check_error_height: number;
  };
  messages: {
    check_error_size: Translation;
    check_error_width: Translation;
    check_error_height: Translation;
  };
};

type ImageInputPlaceholders = {
  withValue?: Translation;
  noValue?: Translation;
};

type ImageInputProps = Omit<InputWrapperProps, 'readOnly' | 'name'> & {
  classNames?: {
    label?: string;
    imageContainer?: string;
    requirements?: string;
    controls?: string;
  };
  buttonClassName?: string;
  name: string;
  value?: File | string | null;
  onChange: (file: File | string | null) => void;
  width?: number | string;
  height?: number | string;
  acceptFormats?: string;
  imageStyle?: React.CSSProperties;
  placeholderIcon?: string;
  keepRatio?: boolean;
  disabled?: boolean;
  disableClassNamePreview?: boolean;
  classNamePreview?: string;
  onBlur?: () => void;
  required?: boolean;
  requirementsComment?: string;
  base64?: boolean;
  checks?: ImageInputChecks;
  placeholders?: ImageInputPlaceholders;
  removable?: boolean;
  resettable?: boolean;
  onRemove?: () => void;
  onReset?: () => void;
};

const ImageInput: React.FC<ImageInputProps> = (props) => {
  const {
    className = '',
    classNames = {},
    buttonClassName = '',
    label = '',
    name = '',
    value = null,
    onChange,
    errorMessage = '',
    warningMessage = '',
    width = 0,
    height = 0,
    acceptFormats = '',
    placeholderIcon = placeholder,
    keepRatio = false,
    onBlur = null,
    disabled = false,
    disableClassNamePreview = false,
    imageStyle = {},
    classNamePreview = '',
    required = false,
    requirementsComment = '',
    base64 = false,
    checks = {},
    placeholders = {},
    removable = true,
    resettable,
    onRemove,
    onReset,
  } = props;
  const [localErrorMessage, setLocalErrorMessage] = useState<Record<string, Translation>>({});
  const lang = useLanguage('COMMON');

  const handleChange = async (file: File | string) => {
    let errors: Record<string, Translation> = {};

    if (!isEmpty(checks) && 'checks' in checks && 'messages' in checks) {
      const imageProps = await getImageProperties(file);

      errors = validate(imageProps.width, imageProps.height, checks as ImageInputChecks);
      setLocalErrorMessage(errors);
    }

    if (isEmpty(errors)) {
      onChange(file);
      onBlur?.();
    }
  };

  const handleRemove = () => {
    onRemove?.();
    onChange(null);
  };

  const placeholderText = value ? placeholders?.withValue ?? lang.REPLACE : placeholders?.noValue ?? lang.UPLOAD;

  return (
    <InputWrapper
      label={label}
      className={cn(b({error: !!errorMessage, warning: !!warningMessage}), className)}
      name={name}
      required={required}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
    >
      <div className={cn(b('controls'), classNames?.controls)}>
        <FileButton
          className={cn(b('file-button', buttonClassName))}
          name={name}
          disabled={disabled}
          base64={base64}
          onChange={handleChange}
          acceptFormats={acceptFormats}
          asWrap={true}
          label={
            <div className={b('container', {preview: !value, 'without-value': !value, 'with-value': !!value})}>
              <ImageContainer
                className={cn(b('image-container', {'without-value': !value}), classNames?.imageContainer)}
                classNamePreview={cn(!disableClassNamePreview && b('preview'), classNamePreview)}
                value={value ? createObjectUrl(value) : (placeholderIcon as unknown as string)}
                width={width}
                height={height}
                keepRatio={!!value && keepRatio}
                imageStyle={value ? imageStyle : {}}
              />
              <span className={b('placeholder-text')}>{placeholderText}</span>
            </div>
          }
        />
        {!disabled && (
          <div className={b('buttons-wrap')}>
            {removable && value && (
              <ConfirmationPopover
                title={lang.DELETE_THE_IMAGE}
                okButtonProps={{
                  label: lang.DELETE_CONFIRM,
                  onClick: handleRemove,
                }}
              >
                <AppButton
                  className={b('button')}
                  title={lang.DELETE}
                  iconConfig={{
                    name: 'close',
                    width: 22,
                    height: 22,
                    className: b('delete-icon'),
                  }}
                  asWrap={true}
                />
              </ConfirmationPopover>
            )}
            {resettable && (
              <AppButton
                className={b('button')}
                title={lang.RESET}
                iconConfig={{
                  name: 'restore',
                  width: 22,
                  height: 22,
                  className: b('reset-icon'),
                }}
                onClick={onReset}
                asWrap
              />
            )}
          </div>
        )}
        {requirementsComment && (
          <p className={cn(b('requirements'), classNames?.requirements)}>{requirementsComment}</p>
        )}
      </div>

      {!isEmpty(localErrorMessage) && (
        <ConfirmationModal
          show={true}
          onConfirm={() => setLocalErrorMessage({})}
          onClose={() => setLocalErrorMessage({})}
          messageClassName={b('message-container')}
          message={Object.values(localErrorMessage).map((item, idx) => (
            <span className={b('message')} key={idx}>
              {item}
            </span>
          ))}
          clientSide={true}
          title={lang.UPLOAD_ERROR}
          buttonConfirm={{
            label: lang.OK,
            color: 'devices',
          }}
        />
      )}
    </InputWrapper>
  );
};

export default ImageInput;
